import { isNilUUID, type UUID } from '@/utils/uuid';
import type { LogoAsset } from '@/types/shared/logo';

/**
 * Runtime instance of a feature attached to a connector.
 *
 * This maps to the adapter's ConnectorFeatureModel but is expressed in
 * UI-friendly types and camelCase property names.
 */
export interface ConnectorFeature {
  /** Meta: read-model revision; backend stamps this for eventual-consistency polling. */
  _rev?: number;
  /** Parent connector id (required for addressing a specific instance). */
  connectorId: UUID;
  /** Provider feature template id (required; composite key with connectorId). */
  featureId: UUID;
  /** Human-friendly name for the feature. */
  name: string;
  /** Short description of what the feature does. */
  description?: string;
  /** Arbitrary runtime options provided by the feature/connector. */
  options?: Record<string, unknown>;
  /** List of requirements this feature needs. */
  requirements: ReadonlyArray<string>;
  /** Whether the feature is currently enabled for the connector. */
  status?: boolean;
  /** Provider name for this feature. */
  providerName?: string;
  /** List of spaces this feature covers. */
  spaces?: ReadonlyArray<string>;
  /** Secrets bound to this feature, keyed by credential slot name. */
  secretIds?: Readonly<Record<string, UUID>>;
  /** Optional client IDs work is pinned to (if non-empty). */
  clientIds?: ReadonlyArray<UUID>;
  /** Denormalized provider feature category: 'collector' runs on a schedule, 'action' is on-demand. */
  type?: 'collector' | 'action';
  /** Whether this feature can be scheduled (false = manual/on-demand only). */
  schedulable?: boolean;
  /** JSON schema for the payload the user must supply before invoking (non-schedulable action features only). */
  payloadSchema?: unknown;
}

/**
 * UI representation of a Connector.
 */
export interface Connector {
  /** Meta: read-model revision; backend stamps this for eventual-consistency polling. */
  _rev?: number;
  id: UUID;
  providerId: UUID;
  name?: string;
  providerName?: string;
  logo?: LogoAsset;
  options?: Record<string, unknown>;
  requirements?: ReadonlyArray<string>;
  /** Structured on-site routing sites (distinct from free-text requirements). */
  requiredSites?: ReadonlyArray<string>;
  status?: boolean;
  is_system?: boolean;
  isInternal?: boolean;
}

/**
 * Lightweight summary used when listing connectors.
 */
export interface ConnectorStats {
  lastSyncAt?: Date;
  recordsCount?: number;
  icon?: string;
}

/**
 * Result payload returned when invoking a connector feature.
 */
export interface InvokeConnectorFeatureResult {
  connectorId: string;
  featureId: string;
  runSequence: string;
  tenantId: string;
}

/**
 * Current invocation sequencing state for a connector feature.
 */
export interface ConnectorFeatureCurrentSequence {
  inFlight: boolean;
  sequenceNum: number;
}

/**
 * Returns the secret id bound to a specific credential slot, ignoring the NIL
 * placeholder backends use to represent "cleared".
 */
export function getBoundSecretId(
  feature: Pick<ConnectorFeature, 'secretIds'>,
  slotName: string
): UUID | undefined {
  const sid = feature.secretIds?.[slotName];
  return sid && !isNilUUID(sid) ? sid : undefined;
}

/**
 * True when the feature has at least one real (non-NIL) bound secret across any
 * of its credential slots.
 */
export function hasBoundSecret(
  feature: Pick<ConnectorFeature, 'secretIds'>
): boolean {
  const secretIds = feature.secretIds;
  if (!secretIds) return false;
  return Object.values(secretIds).some((sid) => sid && !isNilUUID(sid));
}
